import React, { useState } from 'react';
import { SienaguardServiceDetail, ScriptRun } from '../../../src/api/sienaguardService';
import { useTranslation } from 'react-i18next';

interface ServiceDetailProps {
  serviceDetail: SienaguardServiceDetail | null;
}

const PAGE_SIZE = 20;

/** Formatea run_date => dd/mm/yyyy HH:mm:ss (hora local) */
function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');
  const secs = String(date.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}:${secs}`;
}

/** Traduce el status a algo más friendly */
function getStatusLabel(status: string | undefined, t: any): string {
  const st = status?.toLowerCase();
  if (st === 'completed') return t('serviceDetail.statusCompleted');
  if (st === 'failed') return t('serviceDetail.statusFailed');
  if (st === 'warning') return t('serviceDetail.statusWarning');
  return t('serviceDetail.statusUnknown');
}

/** Devuelve el comentario según el estado y los dominios encontrados */
function getComment(run: ScriptRun, t: any): string {
  const st = run.status?.toLowerCase();
  if (st === 'completed') {
    return t('serviceDetail.commentNoSuplantation');
  } else if (st === 'failed') {
    return t('serviceDetail.commentFailed');
  } else if (st === 'warning') {
    const found = run.found_domains || [];
    if (found.length > 0) {
      const domainList = found.map((fd) => fd.domain).join(', ');
      return t('serviceDetail.commentWarningWithDomains', { domainList });
    }
    return t('serviceDetail.commentWarningNoDomains');
  }
  return t('serviceDetail.commentUnknown');
}

const ServiceDetail: React.FC<ServiceDetailProps> = ({ serviceDetail }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  if (!serviceDetail) {
    return (
      <div className="bg-gradient-to-b from-[#0A1723] to-[#050C12] text-white p-4 rounded-xl shadow">
        <p className="text-gray-300">
          {t('serviceDetail.selectAService')}
        </p>
      </div>
    );
  }

  const runs = serviceDetail.runs || [];

  // Paginación
  const totalRuns = runs.length;
  const totalPages = Math.ceil(totalRuns / PAGE_SIZE);
  const currentPage = Math.min(page, totalPages || 1);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const pageRuns = runs.slice(startIndex, endIndex);

  const handlePrev = () => {
    if (currentPage > 1) setPage((p) => p - 1);
  };
  const handleNext = () => {
    if (currentPage < totalPages) setPage((p) => p + 1);
  };

  return (
    <div className="bg-gradient-to-b from-[#0A1723] to-[#050C12] text-white p-4 rounded-xl shadow mb-8">
      {/* Encabezado */}
      <h2 className="text-2xl font-bold mb-4">
        {t('serviceDetail.titleFor', { domain: serviceDetail.domain })}
      </h2>

      {totalRuns === 0 ? (
        <p className="text-sm text-gray-300">{t('serviceDetail.noRuns')}</p>
      ) : (
        <>
          <p className="text-sm mb-2">
            {t('serviceDetail.timesSearched', {
              domain: serviceDetail.domain,
              count: totalRuns
            })}
          </p>

          {/* Tabla estilo ciber */}
          <div className="overflow-x-auto">
            <table className="table-fixed w-full border-collapse text-left text-sm">
              <thead>
                <tr className="bg-[#131F2D] text-white uppercase text-xs">
                  <th className="p-2 border-b border-gray-600 w-1/5">
                    {t('serviceDetail.date')}
                  </th>
                  <th className="p-2 border-b border-gray-600 w-1/5">
                    {t('serviceDetail.status')}
                  </th>
                  <th className="p-2 border-b border-gray-600 w-3/5">
                    {t('serviceDetail.comment')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRuns.map((run, idx) => {
                  const dateFormatted = formatDateTime(run.run_date);
                  const statusLabel = getStatusLabel(run.status, t);
                  const comment = getComment(run, t);

                  return (
                    <tr
                      key={run.id || idx}
                      className="transition-colors border-b border-gray-600 
                                 hover:bg-[#14202E]"
                    >
                      <td className="p-2">
                        {dateFormatted}
                      </td>
                      <td className="p-2">
                        {statusLabel}
                      </td>
                      {/* Permitir varias líneas */}
                      <td className="p-2 whitespace-normal break-words">
                        {comment}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <button
                onClick={handlePrev}
                disabled={currentPage <= 1}
                className="px-3 py-1 bg-[#00FF69] text-black rounded 
                           disabled:opacity-50 hover:bg-[#00e859] transition"
              >
                {t('serviceDetail.prevPage')}
              </button>

              <span className="text-xs text-gray-300">
                {t('serviceDetail.pageXofY', {
                  x: currentPage,
                  y: totalPages
                })}
              </span>

              <button
                onClick={handleNext}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 bg-[#00FF69] text-black rounded 
                           disabled:opacity-50 hover:bg-[#00e859] transition"
              >
                {t('serviceDetail.nextPage')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ServiceDetail;
